import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import hocScrollbar from 'gnos-scrollbar'

import hocConnect from './hocConnect'

import './style.scss'

@hocScrollbar({ smoothScrollbar: true })
@hocConnect
export default class VirtualizedList extends Component {
    static propTypes = {
        afterComponent: PropTypes.element,
        beforeComponent: PropTypes.element,
        forceRender: PropTypes.func.isRequired,
        forceRenderFlag: PropTypes.bool.isRequired,
        height: PropTypes.number.isRequired,
        id: PropTypes.string.isRequired,
        isScrollbarNative: PropTypes.bool.isRequired,
        itemHeight: PropTypes.number.isRequired,
        itemsCount: PropTypes.number.isRequired,
        maxWaitTime: PropTypes.number,
        offset: PropTypes.number,
        positionY: PropTypes.number.isRequired, // from hocScrollbar
        renderItem: PropTypes.func.isRequired,
        scrollDebounceTime: PropTypes.number,
    }

    static defaultProps = {
        afterComponent: null,
        beforeComponent: null,
        offset: 20,
        maxWaitTime: 1000,
        scrollDebounceTime: 250,
    }

    constructor(props) {
        super(props)

        this.state = {
            topIndex: 0,
            bottomIndex: 0,
        }
    }

    componentDidMount() {
        this._isMounted = true

        // first set of 'visible' rows
        this.setScrollState(this.props.positionY)
    }

    shouldComponentUpdate(nextProps, nextState) {
        const state = _.every(['height', 'topIndex', 'bottomIndex'], (key) => this.state[key] === nextState[key])
        const props = _.every(
            ['positionY', 'itemsCount', 'isScrollbarNative'],
            (key) => this.props[key] === nextProps[key]
        )
        const isForceRenderSame = this.props.forceRenderFlag === nextProps.forceRenderFlag

        // not update if forceRender is set from true to false and everything is same
        if (state && props && !isForceRenderSame && this.props.forceRenderFlag && !nextProps.forceRenderFlag)
            return false

        return !(state && props && isForceRenderSame)
    }

    componentDidUpdate(prevProps) {
        const { forceRender, forceRenderFlag, id } = this.props

        if (this.props.positionY !== prevProps.positionY) {
            this.setScrollState(this.props.positionY)
        }

        // after rerender change forceRenderFlag back to false
        if (!prevProps.forceRenderFlag && forceRenderFlag) {
            forceRender(id)
        }
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    // debounced function for set actual 'visible' rows
    setScrollState = _.debounce(
        (scrollOffset = 0) => {
            if (this._isMounted) {
                const { height, itemHeight, offset } = this.props

                // compute actual indexes of list array
                const newTopIndex = _.floor(scrollOffset / itemHeight) - offset
                const newBottomIndex = _.ceil((scrollOffset + height) / itemHeight) + offset

                this.setState({
                    topIndex: newTopIndex,
                    bottomIndex: newBottomIndex,
                })
            }
        },
        this.props.isScrollbarNative ? this.props.scrollDebounceTime / 2 : this.props.scrollDebounceTime,
        {
            leading: true,
            maxWait: this.props.maxWaitTime,
        }
    )

    // render 'visible' rows and empty blocks (before and after)
    renderRows = () => {
        const { afterComponent, beforeComponent, itemsCount, itemHeight, renderItem } = this.props
        const { topIndex, bottomIndex } = this.state

        const top = Math.max(topIndex, 0)
        const bottom = Math.min(bottomIndex, itemsCount)

        const visibleRows = _.map(Array(_.clamp(bottom - top, 0, itemsCount)), (row, index) =>
            renderItem(index + top, false)
        )

        return [
            <div key={'component_before'}>{beforeComponent}</div>,
            <div key={'row_before'} style={{ height: `${top * itemHeight}px` }} />,
            visibleRows,
            <div key={'row_after'} style={{ height: `${(itemsCount - bottom) * itemHeight}px` }} />,
            <div key={'component_after'}>{afterComponent}</div>,
        ]
    }

    render() {
        return this.renderRows()
    }
}
